<script>
import useSharedData from "@/use/SharedData.js";
import useValidation from "@/use/Validation.js";
import useTools from "@/use/Tools.js";
import {computed, onMounted, ref, watch} from "vue";
import {useAbsenceRequestStore} from "@/stores/AbsenceRequest.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useSharedSelectData from "@/use/SharedSelectData.js";
import {useSecurityStore} from "@/stores/Security";
import axios from "axios";
import WCodeGeneration from "@/components/WCodeGeneration.vue";

export default {
    components: {WCodeGeneration},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
        vacation: Boolean,
    },

    setup(props) {
        const {mxRules} = useValidation();
        const {loading, dialog, overlay, inputColor, backgroundColor} = useSharedData();
        const {inputModes} = useSharedSelectData();
        const {$route, $refs} = useTools();
        const {getBack, formatedDate} = useSharedMethod();

        const absenceRequestStore = useAbsenceRequestStore();
        const securityStore = useSecurityStore();

        const readonly = computed(() => {
            return props.viewAction || props.deleteAction;
        });

        const absenceRequest = ref({
            workerId: null,
            inputMode: props.vacation ? "DATE_TO_PERIOD" : null,
            reasonId: props.vacation ? securityStore.userContext.vacationAbsenceReasonId : null,
        });

        function findById(id) {
            loading.value = true;
            axios.get(`/absenceRequests/${id}`).then((response) => {
                absenceRequest.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/absenceRequests', absenceRequest.value).then((response) => {
                    absenceRequestStore.addAbsenceRequest(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/absenceRequests', absenceRequest.value).then((response) => {
                    absenceRequestStore.updateAbsenceRequest(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/absenceRequests/${absenceRequest.value.id}`).then(() => {
                absenceRequestStore.deleteAbsenceRequest(absenceRequest.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        const reasonId = computed(() => absenceRequest.value.reasonId);
        const workerId = computed(() => absenceRequest.value.workerId);
        const period = computed(() => absenceRequest.value.period);
        const startDate = computed(() => absenceRequest.value.startDate);

        const balance = ref(null);
        const loadingBalance = ref(false);

        function getBalance() {
            if (reasonId.value && workerId.value) {
                loadingBalance.value = true;
                axios.get(`/workerBalances/balance`, {
                    params: {
                        workerId: workerId.value,
                        reasonId: reasonId.value,
                    }
                }).then(response => {
                    balance.value = response.data;
                }).finally(() => {
                    loadingBalance.value = false;
                });
            }
        }

        watch([reasonId, workerId], () => getBalance());

        watch([reasonId, startDate, period], () => getEndDate());

        const loadingEndDate = ref(false);

        const endDate = ref(null);

        function getEndDate() {

            if (reasonId && period.value && startDate.value) {
                loadingEndDate.value = true;
                axios.post(`/absenceRequests/getEndDate`, absenceRequest.value).then(response => {
                    endDate.value = response.data;
                }).finally(() => {
                    loadingEndDate.value = false;
                });
            }

        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            absenceRequest,
            inputColor,
            backgroundColor,
            mxRules,
            readonly,
            loading,
            dialog,
            overlay,
            inputModes,
            balance,
            loadingBalance,
            loadingEndDate,
            endDate,
            save,
            update,
            remove,
            getBack,
            formatedDate,
        };
    },
}
;
</script>

<template>
    <v-dialog v-model="dialog" persistent max-width="700" scrollable>
        <v-card>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            ></w-dialog-toolbar>
            <w-overlay
                :loading="loading"
                :overlay="overlay"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            ></w-overlay>

            <v-card-text>
                <v-container fluid>
                    <v-form ref="form" lazy-validation>
                        <w-fieldset :title="$t('common.details')">
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('common.code')" required/>
                                    <w-code-generation
                                        v-model="absenceRequest.code"
                                        entity="ABSENCE_REQUEST"
                                        :load="addAction"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    ></w-code-generation>
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('module.worker')" required/>
                                    <str-worker-autocomplete
                                        v-model="absenceRequest.workerId"
                                        action="SAVE_ABSENCE_REQUEST"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="5" v-if="!vacation">
                                    <w-label :label="$t('module.absence_reason')" required/>
                                    <str-absence-reason-autocomplete
                                        v-model="absenceRequest.reasonId"
                                        :rules="[mxRules.required]"
                                        :readonly="readonly"
                                    />
                                </v-col>
                                <v-col cols="5" v-if="!vacation">
                                    <w-label :label="$t('absence_request.input_mode')" required/>
                                    <w-enum-select
                                        v-model="absenceRequest.inputMode"
                                        :items="inputModes"
                                        :readonly="readonly"
                                        :disabled="!absenceRequest.reasonId"
                                        :rules="[mxRules.required]"
                                        not-clearable
                                    />
                                </v-col>
                                <v-col cols="6" v-if="vacation">
                                    <w-label :label="$t('absence_request.days_number')" required/>
                                    <w-text-field
                                        v-model="absenceRequest.period"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="2" v-if="!readonly && balance">
                                    <v-chip label color="teal" class="mt-6 white--text">
                                        <strong>{{ balance.totalRest }}</strong>
                                    </v-chip>
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset title="Période">
                            <v-row dense>
                                <v-col
                                    cols="6"
                                    v-if="['DATE_TO_DATE', 'DATE_TO_PERIOD'].includes(absenceRequest.inputMode)"
                                >
                                    <w-label :label="$t('absence_request.start_date')" required/>
                                    <w-date-picker
                                        v-model="absenceRequest.startDate"
                                        :rules="[mxRules.required]"
                                        :readonly="readonly"
                                    ></w-date-picker>
                                </v-col>
                                <v-col
                                    cols="6"
                                    v-if="['DATE_TO_DATE'].includes(absenceRequest.inputMode) && !vacation"
                                >
                                    <w-label :label="$t('absence_request.end_date')" required/>
                                    <w-date-picker
                                        v-model="absenceRequest.endDate"
                                        :rules="[mxRules.required]"
                                        :readonly="readonly"
                                    ></w-date-picker>
                                </v-col>
                                <v-col cols="6" v-if="vacation">
                                    <v-chip label color="red" outlined class="mt-6">
                                        <strong>Date Fin : {{ formatedDate(endDate) }} </strong>
                                    </v-chip>
                                </v-col>
                                <v-col
                                    cols="6"
                                    v-if="['DATE_TO_PERIOD'].includes(absenceRequest.inputMode)  && !vacation"
                                >
                                    <w-label :label="$t('absence_request.period')" required/>
                                    <w-text-field
                                        v-model="absenceRequest.period"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col
                                    cols="6"
                                    v-if="['DATE_TO_PERIOD'].includes(absenceRequest.inputMode)  && !vacation"
                                >
                                    <w-label :label="$t('absence_request.end_date')" required/>
                                    <w-text-field :value="formatedDate(endDate)" readonly/>
                                </v-col>


                                <v-col
                                    cols="4"
                                    v-if="['TIME_TO_TIME'].includes(absenceRequest.inputMode)"
                                >
                                    <w-label label="Date" required/>
                                    <w-date-picker
                                        v-model="absenceRequest.startDate"
                                        :rules="[mxRules.required]"
                                        :readonly="readonly"
                                    ></w-date-picker>
                                </v-col>
                                <v-col
                                    cols="4"
                                    v-if="['TIME_TO_TIME'].includes(absenceRequest.inputMode)  && !vacation"
                                >
                                    <w-label label="Heure Début" required/>
                                    <w-time-picker
                                        v-model="absenceRequest.startTime"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col
                                    cols="4"
                                    v-if="['TIME_TO_TIME'].includes(absenceRequest.inputMode)  && !vacation"
                                >
                                    <w-label label="Heure Fin" required/>
                                    <w-time-picker
                                        v-model="absenceRequest.endTime"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.description')"/>
                                    <w-simple-text-area
                                        v-model="absenceRequest.description"
                                        :readonly="readonly"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>